/**
 * Session Countdown Component
 * 
 * Displays a compact session countdown in the header when time is running low.
 * Shows when < 10 minutes remaining, color-coded by urgency.
 */

import React from 'react';
import {
  Box,
  Chip,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  AccessTime as ClockIcon,
  Warning as WarningIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useSessionTimer, useSessionWarning } from '../../contexts/SessionWarningContext';
import { formatTimeRemaining, getUrgencyLevel } from '../../utils/timeFormatters';
// import { logger } from "../../utils/logger";
const SessionCountdown: React.FC = () => {
  const theme = useTheme();
  const { timeRemaining, timeRemainingSeconds } = useSessionTimer();
  const { showWarning } = useSessionWarning();

  // Don't show if no time data or > 10 minutes
  if (!timeRemaining || timeRemaining > 10 * 60 * 1000) {
    return null;
  }

  const urgency = getUrgencyLevel(timeRemaining);
  const formatted = formatTimeRemaining(timeRemaining);

  // Get icon based on urgency
  const getIcon = () => {
    switch (urgency) {
      case 'critical':
        return <ErrorIcon sx={{ fontSize: 18 }} />;
      case 'warning':
        return <WarningIcon sx={{ fontSize: 18 }} />;
      default:
        return <ClockIcon sx={{ fontSize: 18 }} />;
    }
  };

  // Get color based on urgency
  const getColor = () => {
    switch (urgency) {
      case 'critical':
        return theme.palette.error.main;
      case 'warning':
        return theme.palette.warning.main;
      default:
        return theme.palette.info.main;
    }
  };

  const color = getColor();
  const icon = getIcon();

  // Get background color
  const getBgColor = () => {
    switch (urgency) {
      case 'critical':
        return `${theme.palette.error.main}20`;
      case 'warning':
        return `${theme.palette.warning.main}20`;
      default:
        return `${theme.palette.info.main}20`;
    }
  };

  const bgColor = getBgColor();

  return (
    <Tooltip
      title={`Your session will expire in ${formatted}. Click to extend.`}
      arrow
    >
      <Box
        onClick={showWarning}
        sx={{
          cursor: 'pointer',
          '&:hover': {
            transform: 'translateY(-2px)',
            '& .MuiChip-root': {
              boxShadow: `0 4px 12px ${color}40`
            }
          },
          transition: 'transform 0.2s ease'
        }}
      >
        <Chip
          icon={icon}
          label={`Session: ${formatted}`}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: '0.875rem',
            bgcolor: bgColor,
            color: color,
            border: `1.5px solid ${color}`,
            animation: urgency === 'critical' ? 'pulse 2s infinite' : 'none',
            '@keyframes pulse': {
              '0%, 100%': {
                opacity: 1,
                transform: 'scale(1)'
              },
              '50%': {
                opacity: 0.85,
                transform: 'scale(1.02)'
              }
            },
            '& .MuiChip-icon': {
              color: color,
              marginLeft: '8px'
            },
            '& .MuiChip-label': {
              px: 1,
              fontFamily: 'monospace'
            },
            transition: 'all 0.2s ease'
          }}
        />
      </Box>
    </Tooltip>
  );
};

export default SessionCountdown;

